import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RouterService } from './router.service';
import { catchError, map, mergeMap, Observable, of, throwError } from 'rxjs';
import { ApiError } from '../models/error';
import { Request } from '../types/requests/Request';
import { UserGroup } from '../types/users/UserGroup';
import { Assignment } from '../types/requests/assignments/assignment';
import {
    Analysis,
    AnalysisTemplate,
    AnalysisTemplateField,
    AnalysisTemplateFieldValue,
} from '../types/requests/analysis/Analysis';
import { Snapshot } from '../types/requests/analysis/Snapshot';
import { RequestDetailsOptions } from '../types/requests/RequestDetails';

export interface UploadResponse {
    uploadDate: string;
}

@Injectable({
    providedIn: 'root',
})
export class RequestService {
    constructor(
        private _http: HttpClient,
        private routerService: RouterService,
    ) {}

    /**
     * Get all Requests associated with Bill
     */
    getBillRequests(id: string): Observable<Request[]> {
        const url = this.routerService.getApiUrl(`request/bill/${id}`, false);

        return this._http
            .get<Request[]>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Create new Analysis Request
     */
    createRequest(request: Request): Observable<Request> {
        const url = this.routerService.getApiUrl(`request/create`, false);

        return this._http
            .post<Request>(url, request)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Update an Analysis Request
     */
    updateRequest(request: Request): Observable<Request> {
        const url = this.routerService.getApiUrl(`request/save`, false);

        return this._http
            .post<Request>(url, request)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get Request by Id
     */
    getRequest(id: number): Observable<Request> {
        const url = this.routerService.getApiUrl(`request/${id}`, false);

        return this._http
            .get<Request>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get dropdown options for Requests
     */
    getOptions(id: string): Observable<any> {
        const url = this.routerService.getApiUrl(`request/options`, false);

        return this._http
            .post(url, { BillID: id })
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get dropdown options for Requests
     */
    getRequestTypes(): Observable<any> {
        const url = this.routerService.getApiUrl(`requests/types`, false);

        return this._http
            .get(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get Distribution Groups for user
     */
    getGroups(): Observable<UserGroup[]> {
        const url = this.routerService.getApiUrl(`request/distribution`);

        return this._http.get<UserGroup[]>(url).pipe(
            map((groups) =>
                groups.map((group) => {
                    group.groupData = JSON.stringify(
                        JSON.parse(group.groupData, this.toCamelCase),
                    );

                    return group;
                }),
            ),
            catchError((error) => this.handleError(error)),
        );
    }

    /**
     * Save Distribution Groups for user
     */
    saveGroup(group: UserGroup) {
        if (group.groupName === '') {
            return of({
                message: 'Group name cannot be empty',
                success: false,
            });
        }

        const url = this.routerService.getApiUrl(`request/distribution/save`);

        return this._http
            .post(url, group)
            .pipe(catchError((error) => this.handleError(error)));
    }

    deleteGroup(group: UserGroup) {
        const url = this.routerService.getApiUrl(`request/distribution/delete`);

        return this._http
            .post(url, group)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Save User Assignments for Request
     */
    saveAssignments(request: Request) {
        const url = this.routerService.getApiUrl('assignments/save');

        return this._http
            .post(url, request)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Set an assignment as the active task
     */
    setActiveAssignment(assignment: Assignment) {
        const url = this.routerService.getApiUrl(
            `assignments/${assignment.id}/active`,
        );

        return this._http
            .post(url, null)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Submit analysis to next assignment in list
     */
    submitAnalysis(analysis: Analysis) {
        const url = this.routerService.getApiUrl(`analysis/submit`);

        return this._http
            .post(url, analysis)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Return analysis to previous assignment in list
     */
    returnAnalysis(analysis: Analysis) {
        const url = this.routerService.getApiUrl(`analysis/return`);

        return this._http
            .post(url, analysis)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Uploads a File blob
     * @param form File input content encoded into a formData object
     * @returns uploads a document
     */
    upload(form: FormData, analysisId: number): Observable<UploadResponse> {
        const url = this.routerService.getApiUrl(`analysis/${analysisId}/word`);

        return this._http
            .post<UploadResponse>(url, form)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get Word Analysis by Id
     * @param id -> Analysis Id
     * @returns document analysis by document id
     */
    downloadAnalysis(analysisId: number) {
        const url = this.routerService.getApiUrl(
            `analysis/${analysisId}/download`,
        );

        return this._http
            .get(url, {
                reportProgress: true,
                observe: 'response',
                responseType: 'blob',
            })
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Create new Analysis for Request
     */
    createAnalysis(id: number) {
        const url = this.routerService.getApiUrl(`analysis/new`);

        return this._http
            .post(url, { ID: id })
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get Analysis by Request Id
     */
    getAnalysis(id: number): Observable<Analysis> {
        const url = this.routerService.getApiUrl(
            `request/${id}/analysis`,
            false,
        );

        return this._http
            .get<Analysis>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get all Analyses for a Request
     */
    getAnalyses(id: number): Observable<Analysis[]> {
        const url = this.routerService.getApiUrl(
            `request/${id}/analyses`,
            false,
        );

        return this._http
            .get<Analysis[]>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get HTML preview for current analysis
     */
    getAnalysisPreview(id: number): Observable<string> {
        const url = this.routerService.getApiUrl(
            `request/${id}/analysis_preview`,
            false,
        );

        return this._http
            .get(url, { responseType: 'text' })
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Save a field on an analysis
     * @param id -> analysisId
     * @param field -> field to update
     */
    saveAnalysisField(
        id: number,
        field: AnalysisTemplateField,
    ): Observable<AnalysisTemplateField> {
        const url = this.routerService.getApiUrl(
            `analysis/${id}/save/field`,
            false,
        );

        return this._http
            .put<AnalysisTemplateField>(url, field)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Save all fields of an analysis
     * @param id -> analysisId
     * @param analysis -> analysis to update
     */
    saveAnalysisFields(id: number, analysis: Analysis): Observable<Analysis> {
        const url = this.routerService.getApiUrl(`analysis/${id}/save`, false);

        return this._http
            .put<Analysis>(url, analysis)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get all snapshot versions for Request
     */
    getSnapshots(id: number): Observable<Snapshot[]> {
        const url = this.routerService.getApiUrl(
            `analysis/snapshots/${id}`,
            false,
        );

        return this._http
            .get<Snapshot[]>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get all snapshot versions for Request
     */
    getAllSnapshots(id: number): Observable<Snapshot[]> {
        const url = this.routerService.getApiUrl(
            `requests/${id}/snapshots`,
            false,
        );

        return this._http
            .get<Snapshot[]>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get snapshot html preview
     */
    getSnapshotHtml(id: number): Observable<string> {
        const url = this.routerService.getApiUrl(`snapshots/${id}/html`, false);

        return this._http
            .get(url, { responseType: 'text' })
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Compare snapshot text to current analysis
     */
    compareToSnapshot(analysisId: number, leftId: number): Observable<string> {
        const url = this.routerService.getApiUrl(
            `analysis/${analysisId}/snapshot/${leftId}`,
            false,
        );

        return this._http
            .get(url, { responseType: 'text' })
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Compare two snapshot texts
     */
    compareSnapshots(
        olderSnapshotId: number,
        newerSnapshotId: number,
    ): Observable<string> {
        const url = this.routerService.getApiUrl(
            `snapshots/compare/${newerSnapshotId}/${olderSnapshotId}`,
            false,
        );

        return this._http
            .get(url, { responseType: 'text' })
            .pipe(catchError((error) => this.handleError(error)));
    }

    getTemplates(): Observable<AnalysisTemplate[]> {
        const url = this.routerService.getApiUrl(`templates/all`);

        return this._http
            .get<AnalysisTemplate[]>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    getTemplate(id: number): Observable<AnalysisTemplate> {
        const url = this.routerService.getApiUrl(`templates/${id}`);

        return this._http
            .get<AnalysisTemplate>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    uploadTemplate(id: number, data: any): Observable<any> {
        const url = this.routerService.getApiUrl(`templates/${id}/word`);

        return this._http
            .post(url, data)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Print Analysis
     * @param id Analysis Id
     * @returns File to Download
     */
    exportAnalysis(id: number) {
        const url = this.routerService.getApiUrl(`analysis/print/${id}`);

        return this._http
            .get(url, {
                reportProgress: true,
                observe: 'response',
                responseType: 'blob',
            })
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Attempt to account for previous LAS naming conventions
     */
    private toCamelCase(key: any, value: any) {
        if (value && typeof value === 'object') {
            for (var k in value) {
                if (
                    /^(?!ID)[A-Z]/.test(k) &&
                    Object.hasOwnProperty.call(value, k)
                ) {
                    value[k.charAt(0).toLowerCase() + k.substring(1)] =
                        value[k];
                    delete value[k];
                }
            }
        }

        return value;
    }

    private handleError(error: ApiError) {
        return throwError(() => new ApiError('An unexpected error occurred'));
    }
}
